<script setup>
import { readonly, ref } from 'vue';
import AppIcon from '../atoms/app/AppIcon.vue';
import AppLink from '../atoms/app/AppLink.vue';
import TextHeadline from '../atoms/app/text/TextHeadline.vue';
import FacebookIcon from '../../assets/img/social-icons/facebook.svg';
import InstagramIcon from '../../assets/img/social-icons/instagram.svg';
import LinkedInIcon from '../../assets/img/social-icons/linkedin.svg';
import PinterestIcon from '../../assets/img/social-icons/pinterest.svg';
import TikTokIcon from '../../assets/img/social-icons/tiktok.svg';
import YoutubeIcon from '../../assets/img/social-icons/youtube.svg';
import NorikaLogo from '../../assets/img/logos/norika.svg';
import WebOnly from '../webview/WebOnly.vue';
import { track } from '../../utils/tracking';

defineProps({
    variant: {
        default: 'big',
        type: String,
    },
});

const activeHeader = ref(null);

const headers = readonly({
    ABOUT: 0,
    EMPLOYERS: 1,
    PARTNER: 2,
    MYPROFILE: 3,
});

function toggle(index) {
    // Toggle is only needed on mobile.
    activeHeader.value = activeHeader.value === index ? null : index;
}
</script>

<template>
    <WebOnly
        v-bind="$attrs"
        class="o-footer"
        :data-qa="`${variant === 'big' ? 'big footer' : 'small footer'}`"
    >
        <div class="o-footer__container">
            <!-- LINK SECTION -->
            <div
                v-if="variant === 'big'"
                class="o-footer__linkSection"
                data-qa="link section"
            >
                <!-- ÜBER KARRIERE.AT -->
                <div class="o-footer__group">
                    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
                    <div
                        class="o-footer__groupHeader"
                        :class="{ 'o-footer__groupHeader--open': activeHeader === headers.ABOUT }"
                        data-qa="about karriere.at toggle"
                        @click="toggle(headers.ABOUT)"
                    >
                        <TextHeadline
                            variant="base-700"
                            :level="1"
                        >
                            Über karriere.at
                        </TextHeadline>
                        <AppIcon
                            class="o-footer__toggleIcon"
                            :class="{ 'o-footer__toggleIcon--open': activeHeader === headers.ABOUT }"
                            name="arrow-down"
                        />
                    </div>
                    <div
                        :class="{ 'o-footer__groupItems--open': activeHeader === headers.ABOUT }"
                        class="o-footer__groupItems"
                    >
                        <AppLink
                            secondary
                            href="/ueber-uns"
                            class="o-footer__item"
                            data-qa="about us link"
                        >
                            Über uns
                        </AppLink>
                        <AppLink
                            secondary
                            href="/ueber-uns/jobs-bei-karriere-at"
                            class="o-footer__item"
                            data-qa="jobs link"
                        >
                            Jobs bei karriere.at
                        </AppLink>
                        <AppLink
                            secondary
                            href="/presse"
                            class="o-footer__item"
                            data-qa="press link"
                        >
                            Presse
                        </AppLink>
                        <AppLink
                            secondary
                            href="/mobileapp"
                            class="o-footer__item"
                            data-qa="mobile apps link"
                        >
                            Mobile Apps
                        </AppLink>
                        <AppLink
                            secondary
                            href="/blog"
                            class="o-footer__item"
                            data-qa="blog link"
                        >
                            Blog &amp; Tipps
                        </AppLink>
                    </div>
                    <div class="k-c-divider k-c-divider--fine k-c-divider--full o-footer__divider"/>
                </div>
                <!-- FÜR ARBEITGEBER -->
                <div class="o-footer__group">
                    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
                    <div
                        class="o-footer__groupHeader"
                        :class="{ 'o-footer__groupHeader--open': activeHeader === headers.EMPLOYERS }"
                        data-qa="employers toggle"
                        @click="toggle(headers.EMPLOYERS)"
                    >
                        <TextHeadline
                            variant="base-700"
                            :level="1"
                        >
                            Für Arbeitgeber
                        </TextHeadline>
                        <AppIcon
                            class="o-footer__toggleIcon"
                            :class="{ 'o-footer__toggleIcon--open': activeHeader === headers.EMPLOYERS }"
                            name="arrow-down"
                        />
                    </div>
                    <div
                        :class="{
                            'o-footer__groupItems--open': activeHeader === headers.EMPLOYERS,
                        }"
                        class="o-footer__groupItems"
                    >
                        <AppLink
                            secondary
                            href="https://business.karriere.at/"
                            class="o-footer__item"
                            data-qa="products link"
                        >
                            Produkte & Preise
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://business.karriere.at/produkte/stelleninserate"
                            class="o-footer__item"
                            data-qa="advertisement link"
                        >
                            Inserat schalten
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://business.karriere.at/produkte/talentsuche"
                            class="o-footer__item"
                            data-qa="candidate search link"
                        >
                            Talentsuche
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://business.karriere.at/produkte/arbeitgeberprofil"
                            class="o-footer__item"
                            data-qa="employer branding link"
                        >
                            Employer Branding
                        </AppLink>
                    </div>
                    <div class="k-c-divider k-c-divider--fine k-c-divider--full o-footer__divider"/>
                </div>
                <!-- PARTNER UND PORTALE -->
                <div class="o-footer__group">
                    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
                    <div
                        class="o-footer__groupHeader"
                        :class="{ 'o-footer__groupHeader--open': activeHeader === headers.PARTNER }"
                        data-qa="partner and portal toggle"
                        @click="toggle(headers.PARTNER)"
                    >
                        <TextHeadline
                            variant="base-700"
                            :level="1"
                        >
                            Partner und Portale
                        </TextHeadline>
                        <AppIcon
                            class="o-footer__toggleIcon"
                            :class="{ 'o-footer__toggleIcon--open': activeHeader === headers.PARTNER }"
                            name="arrow-down"
                        />
                    </div>
                    <div
                        :class="{
                            'o-footer__groupItems--open': activeHeader === headers.PARTNER,
                        }"
                        class="o-footer__groupItems"
                    >
                        <AppLink
                            secondary
                            href="https://jobradius.at/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="o-footer__item"
                            data-qa="jobradius link"
                        >
                            jobradius.at
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://www.itstellen.at/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="o-footer__item"
                            data-qa="itstellen link"
                        >
                            itstellen.at
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://www.techtalents.at/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="o-footer__item"
                            data-qa="techtalents link"
                        >
                            techtalents.at
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://www.financejobs.at/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="o-footer__item"
                            data-qa="financejobs link"
                        >
                            financejobs.at
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://www.salescareer.at/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="o-footer__item"
                            data-qa="salescareer link"
                        >
                            salescareer.at
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://www.jobs.at/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="o-footer__item"
                            data-qa="jobsat link"
                        >
                            jobs.at
                        </AppLink>
                        <AppLink
                            secondary
                            href="https://hokify.at/"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="o-footer__item"
                            data-qa="hokify link"
                        >
                            hokify Mobile Jobsuche
                        </AppLink>
                    </div>
                    <div class="k-c-divider k-c-divider--fine k-c-divider--full o-footer__divider"/>
                </div>
                <!-- MEIN PROFIL -->
                <div class="o-footer__group">
                    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
                    <div
                        class="o-footer__groupHeader"
                        :class="{ 'o-footer__groupHeader--open': activeHeader === headers.MYPROFILE }"
                        data-qa="profile toggle"
                        @click="toggle(headers.MYPROFILE)"
                    >
                        <TextHeadline
                            variant="base-700"
                            :level="1"
                        >
                            Mein Profil
                        </TextHeadline>
                        <AppIcon
                            class="o-footer__toggleIcon"
                            :class="{ 'o-footer__toggleIcon--open': activeHeader === headers.MYPROFILE }"
                            name="arrow-down"
                        />
                    </div>
                    <div
                        :class="{
                            'o-footer__groupItems--open': activeHeader === headers.MYPROFILE,
                        }"
                        class="o-footer__groupItems"
                    >
                        <AppLink
                            secondary
                            href="/profil/lebenslauf"
                            class="o-footer__item"
                            data-qa="cv link"
                        >
                            Lebenslauf
                        </AppLink>
                        <AppLink
                            secondary
                            href="/jobalarm"
                            class="o-footer__item"
                            data-qa="job alarm link"
                        >
                            Job-Alarm
                        </AppLink>
                        <AppLink
                            secondary
                            href="/watchlist"
                            class="o-footer__item"
                            data-qa="watchlist link"
                        >
                            Meine Jobs
                        </AppLink>
                        <AppLink
                            secondary
                            href="/company-follow"
                            class="o-footer__item"
                            data-qa="company follow link"
                        >
                            Meine Firmen
                        </AppLink>
                    </div>
                    <div class="k-c-divider k-c-divider--fine k-c-divider--full o-footer__divider"/>
                </div>
            </div>
            <!-- BOTTOM -->
            <div
                class="o-footer__bottom"
                :class="{
                    [`o-footer__bottom--${variant}`]: variant,
                }"
            >
                <!-- SOCIAL MEDIA -->
                <div
                    v-if="variant === 'big'"
                    class="o-footer__socialMediaIcons"
                    data-qa="social media"
                >
                    <AppLink
                        href="https://www.instagram.com/karriere.at"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="zum Instagram Profil von karriere.at"
                        data-qa="instagram link"
                    >
                        <InstagramIcon
                            class="o-footer__socialMedia o-footer__socialMedia--instagram"
                            alt="Instagram"
                        />
                    </AppLink>
                    <AppLink
                        href="https://www.linkedin.com/company/karriere-at/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="zum LinkedIn Profil von karriere.at"
                        data-qa="linkedin link"
                    >
                        <LinkedInIcon
                            class="o-footer__socialMedia o-footer__socialMedia--linkedin"
                            alt="LinkedIn"
                        />
                    </AppLink>
                    <AppLink
                        href="https://www.facebook.com/karriere.at"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="zum Facebook Profil von karriere.at"
                        data-qa="facebook link"
                    >
                        <FacebookIcon
                            class="o-footer__socialMedia o-footer__socialMedia--facebook"
                            alt="Facebook"
                        />
                    </AppLink>
                    <AppLink
                        href="https://www.tiktok.com/@karriere.at"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="zum TikTok Profil von karriere.at"
                        data-qa="tiktok link"
                    >
                        <TikTokIcon
                            class="o-footer__socialMedia o-footer__socialMedia--tiktok"
                            alt="LinkedIn"
                        />
                    </AppLink>
                    <AppLink
                        href="https://www.youtube.com/user/karriereatchannel"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="zum YouTube Kanal von karriere.at"
                        data-qa="youtube link"
                    >
                        <YoutubeIcon
                            class="o-footer__socialMedia o-footer__socialMedia--youtube"
                            alt="Youtube"
                        />
                    </AppLink>
                    <AppLink
                        href="https://www.pinterest.at/karriereat/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="zum Pinterest Board von karriere.at"
                        data-qa="pinterest link"
                    >
                        <PinterestIcon
                            class="o-footer__socialMedia o-footer__socialMedia--pinterest"
                            alt="Pinterest"
                        />
                    </AppLink>
                </div>
                <!-- IMPRINT AND COPYRIGHT -->
                <div class="flex gap-m flex-col justify-center items-center m:justify-between m:flex-wrap m:flex-row">
                    <div class="flex gap-l flex-col items-center justify-center m:flex-row m:justify-start m:flex-wrap">
                        <ul class="flex gap-l">
                            <li>
                                <AppLink
                                    secondary
                                    href="/lp/faqs"
                                    data-qa="faq link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Häufige Fragen
                                </AppLink>
                            </li>
                            <li>
                                <AppLink
                                    secondary
                                    href="/impressum"
                                    data-qa="imprint link"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Impressum & Kontakt
                                </AppLink>
                            </li>
                            <li>
                                <AppLink
                                    secondary
                                    href="/agb"
                                    data-qa="agb link"
                                    target="_blank"
                                >
                                    AGB
                                </AppLink>
                            </li>
                        </ul>
                        <ul class="flex gap-l flex-wrap justify-center m:justify-start m:flex-nowrap">
                            <li>
                                <AppLink
                                    secondary
                                    href="/agb-datenschutz"
                                    data-qa="gdpr link"
                                    target="_blank"
                                >
                                    Datenschutz
                                </AppLink>
                            </li>
                            <li>
                                <AppLink
                                    secondary
                                    href="/barrierefreiheit"
                                    data-qa="accessibility link"
                                    target="_blank"
                                >
                                    Barrierefreiheit
                                </AppLink>
                            </li>
                            <li>
                                <AppLink
                                    secondary
                                    href="/code-of-conduct"
                                    data-qa="code of conduct link"
                                    target="_blank"
                                >
                                    Code of Conduct
                                </AppLink>
                            </li>
                            <li class="text-center w-full m:text-left m:w-auto text-gray-900">
                                &copy; {{ (new Date()).getFullYear() }} karriere.at GmbH
                            </li>
                        </ul>
                    </div>
                    <a
                        aria-label="Norika Group Logo"
                        href="https://norika.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="max-w-[200px]"
                        @click="track({
                            element: 'FOOT: norika',
                            elementDetail: null,
                        })"
                    >
                        <NorikaLogo class="w-full text-gray-900 hover:text-gray-600"/>
                    </a>
                </div>
            </div>
        </div>
    </WebOnly>
</template>

<style lang="scss">
@use '~@karriereat/global-styles/scss/components/link/mixins' as link;
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/settings/breakpoint';
@use '~@karriereat/global-styles/scss/tools/wrap';

$visual-correction-x: 6px;

.o-footer {
    width: 100%;
    background-color: var(--k-white);

    &__container {
        @media (min-width: breakpoint.$k-breakpoint--m) {
            @include wrap.k-wrap();
        }
    }

    &__linkSection {
        display: flex;
        flex-direction: column;
        padding-bottom: spacing.$k-spacing--2xl;

        @media (min-width: breakpoint.$k-breakpoint--m) {
            flex-direction: row;
            justify-content: space-between;
            padding-right: spacing.$k-spacing--5xl;
        }
    }

    &__group {
        /* Needed for flexbox in o-footer__linkSection. */
    }

    &__toggleIcon {
        width: spacing.$k-spacing--l;
        height: auto;
        color: var(--k-gray-900);
        transform: rotate(0deg);

        &--open {
            transform: rotate(180deg);
        }

        @media (min-width: breakpoint.$k-breakpoint--m) {
            display: none;
        }
    }

    &__groupHeader {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: spacing.$k-spacing--m spacing.$k-spacing--l 0;
        cursor: pointer;

        &--open {
            padding-bottom: spacing.$k-spacing--m;
        }

        @media (min-width: breakpoint.$k-breakpoint--m) {
            padding-top: spacing.$k-spacing--3xl;
            padding-bottom: spacing.$k-spacing--m;
            padding-left: 0;
            pointer-events: none;
        }
    }

    &__groupItems {
        display: none;
        flex-direction: column;
        padding-left: spacing.$k-spacing--l;
        align-items: flex-start;

        @media (min-width: breakpoint.$k-breakpoint--m) {
            display: flex;
            padding-left: 0;
        }

        &--open {
            display: flex;
        }
    }

    &__divider {
        margin-top: spacing.$k-spacing--l;
        background-color: transparent !important;

        @media (min-width: breakpoint.$k-breakpoint--m) {
            display: none;
        }
    }

    &__item:not(:last-child) {
        padding-bottom: spacing.$k-spacing--m;
    }

    &__bottom {
        display: flex;
        flex-direction: column;

        &--big {
            padding-bottom: spacing.$k-spacing--2xl;
        }

        &--small {
            padding-top: spacing.$k-spacing--xl;
            padding-bottom: spacing.$k-spacing--xl;
        }
    }

    &__socialMediaIcons {
        display: flex;
        justify-content: center;
        margin-bottom: spacing.$k-spacing--l;
        margin-left: spacing.$k-spacing--s;
        gap: spacing.$k-spacing--s;

        @media (min-width: breakpoint.$k-breakpoint--m) {
            justify-content: flex-start;
            margin-left: 0;
            padding: spacing.$k-spacing--xs;
        }
    }

    &__socialMedia {
        display: inline-block;
        fill: var(--k-gray-900);
        @include link.k-c-link(var(--k-gray-900), var(--k-gray-600), var(--k-gray-900));

        &--facebook {
            &:hover {
                fill: var(--k-social-facebook);
            }
        }

        &--instagram {
            &:hover {
                fill: var(--k-social-instagram);
            }
        }

        &--youtube {
            &:hover {
                fill: var(--k-social-youtube);
            }
        }

        &--x {
            margin-top: $visual-correction-x;

            &:hover {
                fill: var(--k-social-x);
            }
        }

        &--pinterest {
            &:hover {
                fill: var(--k-social-pinterest);
            }
        }

        &--linkedin {
            &:hover {
                fill: var(--k-social-linkedin);
            }
        }

        &--tiktok {
            &:hover {
                fill: var(--k-social-tiktok);
            }
        }
    }
}
</style>
